'use client';

import { forwardRef, type ReactNode } from 'react';
import { Menu as MenuPrimitive } from '@base-ui/react';
import { ChevronRight } from '../../../icons';
import { cn } from '../../../lib/utils';
import { Icon, type SvgIcon } from '../../icon/icon';
import { menuItemVariants, MENU_ICON_SIZE } from '../shared/menu-item-variants';
import { useMenuSize } from '../shared/menu-size-context';
import { PopoverMenuSurface, type PopoverMenuSurfaceProps } from './popover-menu';

export const PopoverMenuSub = ({ ...props }: MenuPrimitive.SubmenuRoot.Props) => (
  <MenuPrimitive.SubmenuRoot {...props} />
);

export type PopoverMenuSubTriggerProps = Omit<
  MenuPrimitive.SubmenuTrigger.Props,
  'className' | 'children'
> & {
  children?: ReactNode;
  className?: string;
  icon?: SvgIcon;
};

export const PopoverMenuSubTrigger = forwardRef<HTMLElement, PopoverMenuSubTriggerProps>(
  ({ className, children, icon, ...props }, ref) => {
    const { size } = useMenuSize();

    return (
      <MenuPrimitive.SubmenuTrigger
        ref={ref}
        data-slot="popover-menu-sub-trigger"
        className={cn(menuItemVariants({ size }), 'data-popup-open:bg-surface-hover', className)}
        {...props}>
        {icon ? <Icon icon={icon} size={MENU_ICON_SIZE[size]} /> : null}
        {children}
        <Icon icon={ChevronRight} size={MENU_ICON_SIZE[size]} className="ml-auto" />
      </MenuPrimitive.SubmenuTrigger>
    );
  },
);
PopoverMenuSubTrigger.displayName = 'PopoverMenuSubTrigger';

export type PopoverMenuSubContentProps = PopoverMenuSurfaceProps;

export const PopoverMenuSubContent = ({
  side = 'right',
  align = 'start',
  sideOffset = 4,
  // -8 cancels the popup padding so the first submenu row lines up with the trigger row
  alignOffset = -8,
  ...props
}: PopoverMenuSubContentProps) => (
  <PopoverMenuSurface
    data-slot="popover-menu-sub-content"
    side={side}
    align={align}
    sideOffset={sideOffset}
    alignOffset={alignOffset}
    {...props}
  />
);
